<template>
  <AppLayout>
    <div class="p-4 md:p-6">

      <!-- Header (desktop only) -->
      <div class="hidden md:flex items-center justify-between mb-2">
        <div>
          <h1 class="text-2xl font-bold text-slate-800 dark:text-white">Users</h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm mt-0.5">
            {{ filteredUsers.length }} of {{ props.users.length }} user{{ props.users.length !== 1 ? 's' : '' }}
          </p>
        </div>
        <div class="flex items-center gap-3">
          <!-- Status Filter Dropdown -->
          <select v-model="statusFilter" @change="changeStatus"
            class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                   text-slate-700 dark:text-slate-200 text-sm font-medium rounded-xl px-4 py-2.5
                   focus:outline-none focus:ring-2 focus:ring-rose-500 transition cursor-pointer">
            <option value="active">Active Users</option>
            <option value="deleted">Deleted Users</option>
            <option value="all">All Users</option>
          </select>

          <!-- Include Data Reset Checkbox -->
          <label class="flex items-center gap-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                        rounded-xl px-4 py-2.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/80 transition">
            <input type="checkbox" v-model="includeDataReset" @change="changeStatus"
              class="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-rose-500
                     focus:ring-2 focus:ring-rose-500 focus:ring-offset-0 cursor-pointer" />
            <span class="text-sm font-medium text-slate-700 dark:text-slate-200">Include data reset entries</span>
          </label>

          <button @click="showModal = true"
            class="flex items-center gap-2 bg-rose-500 hover:bg-rose-400 active:scale-95 text-white
                   text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm shadow-rose-500/30 transition">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
            </svg>
            <span class="hidden sm:inline">New User</span>
            <span class="sm:hidden">New</span>
          </button>
        </div>
      </div>

      <!-- ── MOBILE HEADER + SEARCH ── -->
      <div class="md:hidden -mx-4 -mt-4 mb-1">
        <!-- Rose gradient hero -->
        <div class="bg-gradient-to-br from-rose-600 via-rose-500 to-pink-500 px-4 pt-3 pb-6">
          <div class="flex items-center justify-between mb-3">
            <div>
              <h1 class="text-xl font-bold text-white">Users</h1>
              <p class="text-rose-200 text-xs mt-0.5">
                {{ filteredUsers.length }} of {{ props.users.length }} user{{ props.users.length !== 1 ? 's' : '' }}
              </p>
            </div>
            <button @click="showModal = true"
              class="flex items-center gap-1.5 bg-white/20 active:bg-white/30 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
              </svg>
              New
            </button>
          </div>
        </div>

        <!-- Search card pulls up over hero -->
        <div class="bg-gray-50 dark:bg-slate-950 rounded-t-3xl -mt-4 px-4 pt-3 pb-2">
          <!-- Status Filter -->
          <select v-model="statusFilter" @change="changeStatus"
            class="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                   text-slate-700 dark:text-slate-200 text-sm font-medium rounded-2xl px-4 py-3 mb-2
                   focus:outline-none focus:ring-2 focus:ring-rose-500 shadow-sm transition cursor-pointer">
            <option value="active">Active Users</option>
            <option value="deleted">Deleted Users</option>
            <option value="all">All Users</option>
          </select>

          <!-- Include Data Reset Checkbox -->
          <label class="flex items-center gap-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                        rounded-2xl px-4 py-3 mb-2 cursor-pointer active:bg-slate-50 dark:active:bg-slate-800/80 transition">
            <input type="checkbox" v-model="includeDataReset" @change="changeStatus"
              class="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-rose-500
                     focus:ring-2 focus:ring-rose-500 focus:ring-offset-0 cursor-pointer" />
            <span class="text-sm font-medium text-slate-700 dark:text-slate-200">Include data reset entries</span>
          </label>

          <!-- Search input -->
          <div class="relative mb-2">
            <svg class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z"/>
            </svg>
            <input v-model="search" type="text" placeholder="Search by email, name or business..."
              class="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                     text-slate-800 dark:text-white placeholder-slate-400 rounded-2xl pl-10 pr-10 py-3 text-sm
                     focus:outline-none focus:ring-2 focus:ring-rose-500 shadow-sm transition" />
            <button v-if="search" @click="search = ''"
              class="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Flash -->
      <div v-if="flash.notice" class="mx-4 md:mx-0 mb-2 px-4 py-3 rounded-xl bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 text-sm font-medium">
        {{ flash.notice }}
      </div>
      <div v-if="flash.alert" class="mx-4 md:mx-0 mb-2 px-4 py-3 rounded-xl bg-rose-50 dark:bg-rose-950 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 text-sm font-medium">
        {{ flash.alert }}
      </div>

      <!-- Empty state -->
      <div v-if="filteredUsers.length === 0" class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 py-16 text-center">
        <svg class="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"/>
        </svg>
        <p class="text-sm font-medium text-slate-400">{{ search ? 'No users match your search.' : 'No users yet.' }}</p>
      </div>

      <!-- Desktop Table -->
      <div v-else>
        <div class="hidden md:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-visible">
          <table class="w-full text-sm whitespace-nowrap">
              <thead>
                <tr class="border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                  <th class="text-left text-[10px] font-semibold text-slate-400 uppercase tracking-wide px-5 py-3.5">#</th>
                  <th @click="sortBy('email')" class="text-left text-[10px] font-semibold uppercase tracking-wide px-4 py-3.5 cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-200" :class="sortCol === 'email' ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'">
                    <span class="inline-flex items-center gap-1">User <SortIcon :active="sortCol === 'email'" :asc="sortDir === 'asc'" /></span>
                  </th>
                  <th @click="sortBy('invoices_count')" class="text-center text-[10px] font-semibold uppercase tracking-wide px-4 py-3.5 cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-200" :class="sortCol === 'invoices_count' ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'">
                    <span class="inline-flex items-center justify-center gap-1">Invoices <SortIcon :active="sortCol === 'invoices_count'" :asc="sortDir === 'asc'" /></span>
                  </th>
                  <th @click="sortBy('payments_count')" class="text-center text-[10px] font-semibold uppercase tracking-wide px-4 py-3.5 cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-200" :class="sortCol === 'payments_count' ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'">
                    <span class="inline-flex items-center justify-center gap-1">Payments <SortIcon :active="sortCol === 'payments_count'" :asc="sortDir === 'asc'" /></span>
                  </th>
                  <th @click="sortBy('customers_count')" class="text-center text-[10px] font-semibold uppercase tracking-wide px-4 py-3.5 cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-200" :class="sortCol === 'customers_count' ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'">
                    <span class="inline-flex items-center justify-center gap-1">Customers <SortIcon :active="sortCol === 'customers_count'" :asc="sortDir === 'asc'" /></span>
                  </th>
                  <th @click="sortBy('products_count')" class="text-center text-[10px] font-semibold uppercase tracking-wide px-4 py-3.5 cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-200" :class="sortCol === 'products_count' ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'">
                    <span class="inline-flex items-center justify-center gap-1">Products <SortIcon :active="sortCol === 'products_count'" :asc="sortDir === 'asc'" /></span>
                  </th>
                  <th @click="sortBy('super_admin')" class="text-left text-[10px] font-semibold uppercase tracking-wide px-4 py-3.5 cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-200" :class="sortCol === 'super_admin' ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'">
                    <span class="inline-flex items-center gap-1">Role <SortIcon :active="sortCol === 'super_admin'" :asc="sortDir === 'asc'" /></span>
                  </th>
                  <th @click="sortBy('sign_in_count')" class="text-center text-[10px] font-semibold uppercase tracking-wide px-4 py-3.5 cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-200" :class="sortCol === 'sign_in_count' ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'">
                    <span class="inline-flex items-center justify-center gap-1">Logins <SortIcon :active="sortCol === 'sign_in_count'" :asc="sortDir === 'asc'" /></span>
                  </th>
                  <th @click="sortBy('last_sign_in_at')" class="text-left text-[10px] font-semibold uppercase tracking-wide px-4 py-3.5 cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-200" :class="sortCol === 'last_sign_in_at' ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'">
                    <span class="inline-flex items-center gap-1">Last Seen <SortIcon :active="sortCol === 'last_sign_in_at'" :asc="sortDir === 'asc'" /></span>
                  </th>
                  <th class="text-left text-[10px] font-semibold text-slate-400 uppercase tracking-wide px-4 py-3.5"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(user, idx) in displayedUsers" :key="user.id"
                  class="border-b border-slate-50 dark:border-slate-800/50 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition">

                  <!-- # -->
                  <td class="px-5 py-3.5 text-xs text-slate-400 font-medium">{{ idx + 1 }}</td>

                  <!-- User info (all in one column) -->
                  <td class="px-4 py-3.5">
                    <div class="flex items-center gap-3">
                      <div class="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                        :class="user.super_admin ? 'bg-rose-500' : 'bg-indigo-500'">
                        {{ (user.email || '?').charAt(0).toUpperCase() }}
                      </div>
                      <div class="min-w-0">
                        <p class="text-sm font-semibold text-slate-800 dark:text-white truncate">{{ user.email }}</p>
                        <p v-if="user.name || user.business_name" class="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {{ [user.name, user.business_name].filter(Boolean).join(' · ') }}
                        </p>
                        <p v-if="user.business_phone_number || user.business_address" class="text-xs text-slate-400 truncate">
                          {{ [user.business_phone_number, user.business_address].filter(Boolean).join(' · ') }}
                        </p>
                        <p class="text-xs text-slate-400 dark:text-slate-500 truncate">Joined {{ formatDate(user.created_at) }}</p>
                      </div>
                    </div>
                  </td>

                  <!-- Invoices count -->
                  <td class="px-4 py-3.5 text-center">
                    <span class="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-lg text-xs font-semibold bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400">
                      {{ user.invoices_count }}
                    </span>
                  </td>

                  <!-- Payments count -->
                  <td class="px-4 py-3.5 text-center">
                    <span class="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-lg text-xs font-semibold bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400">
                      {{ user.payments_count }}
                    </span>
                  </td>

                  <!-- Customers count -->
                  <td class="px-4 py-3.5 text-center">
                    <span class="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-lg text-xs font-semibold bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400">
                      {{ user.customers_count }}
                    </span>
                  </td>

                  <!-- Products count -->
                  <td class="px-4 py-3.5 text-center">
                    <span class="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-lg text-xs font-semibold bg-violet-50 dark:bg-violet-950 text-violet-600 dark:text-violet-400">
                      {{ user.products_count }}
                    </span>
                  </td>

                  <!-- Role -->
                  <td class="px-4 py-3.5">
                    <span class="inline-flex items-center text-[10px] font-semibold px-2.5 py-1 rounded-full"
                      :class="user.super_admin
                        ? 'bg-rose-50 dark:bg-rose-950 text-rose-600 dark:text-rose-400'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'">
                      {{ user.super_admin ? 'Super Admin' : 'User' }}
                    </span>
                  </td>

                  <!-- Logins -->
                  <td class="px-4 py-3.5 text-center">
                    <span class="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                      {{ user.sign_in_count || 0 }}
                    </span>
                  </td>

                  <!-- Last seen -->
                  <td class="px-4 py-3.5 text-xs text-slate-500 dark:text-slate-400">
                    <span :title="formatDate(user.last_sign_in_at)">{{ timeAgo(user.last_sign_in_at) }}</span>
                  </td>

                  <!-- Login as -->
                  <td class="px-4 py-3.5">
                    <form v-if="!user.super_admin" method="post" :action="`/admin/impersonation`" @submit.prevent="loginAs(user.id)">
                      <button type="submit"
                        class="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900 transition">
                        <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9"/>
                        </svg>
                        Login as
                      </button>
                    </form>
                    <span v-else class="text-xs text-slate-300 dark:text-slate-600">—</span>
                  </td>

                </tr>
              </tbody>
            </table>
          </div>

        <!-- Mobile Cards -->
        <div class="md:hidden space-y-2.5">
          <div v-for="(user, idx) in displayedUsers" :key="user.id"
            class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden">

            <!-- Card header -->
            <div class="flex items-center gap-3 px-4 py-3.5 border-b border-slate-100 dark:border-slate-800">
              <div class="w-11 h-11 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
                :class="user.super_admin ? 'bg-rose-500' : 'bg-indigo-500'">
                {{ (user.email || '?').charAt(0).toUpperCase() }}
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-bold text-slate-800 dark:text-white truncate">{{ user.email }}</p>
                <p v-if="user.name || user.business_name" class="text-xs text-slate-500 dark:text-slate-400 truncate">
                  {{ [user.name, user.business_name].filter(Boolean).join(' · ') }}
                </p>
              </div>
              <span class="inline-flex items-center text-[10px] font-semibold px-2.5 py-1 rounded-full shrink-0"
                :class="user.super_admin
                  ? 'bg-rose-50 dark:bg-rose-950 text-rose-600 dark:text-rose-400'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'">
                {{ user.super_admin ? 'Admin' : 'User' }}
              </span>
            </div>

            <!-- Card content -->
            <div class="px-4 py-3 space-y-2">
              <!-- Stats grid -->
              <div class="grid grid-cols-4 gap-2">
                <div class="text-center">
                  <p class="text-[9px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Invoices</p>
                  <span class="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-lg text-xs font-semibold bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400">
                    {{ user.invoices_count }}
                  </span>
                </div>
                <div class="text-center">
                  <p class="text-[9px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Payments</p>
                  <span class="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-lg text-xs font-semibold bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400">
                    {{ user.payments_count }}
                  </span>
                </div>
                <div class="text-center">
                  <p class="text-[9px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Customers</p>
                  <span class="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-lg text-xs font-semibold bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400">
                    {{ user.customers_count }}
                  </span>
                </div>
                <div class="text-center">
                  <p class="text-[9px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Products</p>
                  <span class="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-lg text-xs font-semibold bg-violet-50 dark:bg-violet-950 text-violet-600 dark:text-violet-400">
                    {{ user.products_count }}
                  </span>
                </div>
              </div>

              <!-- Additional info -->
              <div class="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 pt-1">
                <div>
                  <span class="font-medium">{{ user.sign_in_count || 0 }}</span> logins
                  <span class="mx-1.5">·</span>
                  <span>{{ timeAgo(user.last_sign_in_at) }}</span>
                </div>
              </div>

              <p v-if="user.business_phone_number || user.business_address" class="text-xs text-slate-400 truncate">
                {{ [user.business_phone_number, user.business_address].filter(Boolean).join(' · ') }}
              </p>
            </div>

            <!-- Action button -->
            <div v-if="!user.super_admin" class="border-t border-slate-100 dark:border-slate-800 px-4 py-3">
              <form method="post" :action="`/admin/impersonation`" @submit.prevent="loginAs(user.id)">
                <button type="submit"
                  class="w-full flex items-center justify-center gap-2 text-sm font-semibold bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 px-4 py-2.5 rounded-xl transition active:scale-95">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9"/>
                  </svg>
                  Login as {{ user.email.split('@')[0] }}
                </button>
              </form>
            </div>

          </div>
        </div>
      </div>

      <!-- Create User Modal -->
      <Teleport to="body">
        <div v-if="showModal" class="fixed inset-0 z-50 flex items-end md:items-center justify-center">
          <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="closeModal" />
          <div class="relative w-full md:max-w-md bg-white dark:bg-slate-900 rounded-t-3xl md:rounded-2xl
                      border border-slate-100 dark:border-slate-800 shadow-2xl p-6 z-10">
            <div class="flex items-center justify-between mb-5">
              <h2 class="text-base font-bold text-slate-800 dark:text-white">Create New User</h2>
              <button @click="closeModal" class="w-8 h-8 flex items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <form @submit.prevent="submitForm" class="space-y-4">
              <div>
                <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Email *</label>
                <input v-model="form.email" type="email" required placeholder="user@example.com"
                  class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                         text-slate-800 dark:text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500" />
              </div>
              <div>
                <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Full Name</label>
                <input v-model="form.name" type="text" placeholder="John Doe"
                  class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                         text-slate-800 dark:text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500" />
              </div>
              <div>
                <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Business Name</label>
                <input v-model="form.business_name" type="text" placeholder="Acme Ltd."
                  class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                         text-slate-800 dark:text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500" />
              </div>
              <div>
                <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Business Address</label>
                <input v-model="form.business_address" type="text" placeholder="123 Main St, Lahore"
                  class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                         text-slate-800 dark:text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500" />
              </div>
              <div>
                <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Phone Number</label>
                <input v-model="form.business_phone_number" type="tel" placeholder="+92-300-1234567"
                  class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                         text-slate-800 dark:text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500" />
              </div>
              <div>
                <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Password *</label>
                <input v-model="form.password" type="password" required placeholder="Min 6 characters"
                  class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                         text-slate-800 dark:text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500" />
              </div>
              <div>
                <label class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Confirm Password *</label>
                <input v-model="form.password_confirmation" type="password" required placeholder="Repeat password"
                  class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                         text-slate-800 dark:text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500" />
              </div>
              <button type="submit" :disabled="submitting"
                class="w-full bg-rose-500 hover:bg-rose-600 disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition text-sm">
                {{ submitting ? 'Creating...' : 'Create User' }}
              </button>
            </form>
          </div>
        </div>
      </Teleport>

    </div>
  </AppLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import SortIcon from '@/components/SortIcon.vue'

const props = defineProps({
  users: { type: Array, default: () => [] },
  sort: { type: String, default: 'created_at' },
  dir: { type: String, default: 'desc' },
  status: { type: String, default: 'active' },
  include_data_reset: { type: Boolean, default: false }
})

const page = usePage()
const flash = computed(() => page.props.flash || {})
const businessName = computed(() => page.props.auth?.user?.business_name?.trim() || 'Master Ledger')

onMounted(() => { document.title = `Admin | ${businessName.value}` })

const search  = ref('')
const sortCol = ref(props.sort)
const sortDir = ref(props.dir)
const statusFilter = ref(props.status)
const includeDataReset = ref(props.include_data_reset)

function changeStatus() {
  router.visit(`/admin/users?status=${statusFilter.value}&include_data_reset=${includeDataReset.value}`, {
    preserveScroll: true,
    only: ['users', 'status', 'include_data_reset']
  })
}

const filteredUsers = computed(() => {
  const q = search.value.trim().toLowerCase()
  if (!q) return props.users
  return props.users.filter(u =>
    (u.email || '').toLowerCase().includes(q) ||
    (u.name || '').toLowerCase().includes(q) ||
    (u.business_name || '').toLowerCase().includes(q)
  )
})

const displayedUsers = computed(() => {
  const col = sortCol.value
  const asc = sortDir.value === 'asc'
  return [...filteredUsers.value].sort((a, b) => {
    let av = a[col] ?? ''
    let bv = b[col] ?? ''
    // numeric cols
    if (['invoices_count','payments_count','customers_count','products_count','sign_in_count'].includes(col)) {
      av = Number(av); bv = Number(bv)
    } else if (col === 'super_admin') {
      av = av ? 1 : 0; bv = bv ? 1 : 0
    } else if (col === 'last_sign_in_at' || col === 'created_at') {
      av = av ? new Date(av).getTime() : 0
      bv = bv ? new Date(bv).getTime() : 0
    } else {
      av = String(av).toLowerCase(); bv = String(bv).toLowerCase()
    }
    if (av < bv) return asc ? -1 : 1
    if (av > bv) return asc ? 1 : -1
    return 0
  })
})

function sortBy(col) {
  if (sortCol.value === col) {
    sortDir.value = sortDir.value === 'desc' ? 'asc' : 'desc'
  } else {
    sortCol.value = col
    sortDir.value = 'desc'
  }
}

function formatDate(dateStr) {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

function timeAgo(dateStr) {
  if (!dateStr) return 'Never'
  const seconds = Math.floor((Date.now() - new Date(dateStr)) / 1000)
  if (seconds < 60)                        return 'Just now'
  if (seconds < 3600)                      return Math.floor(seconds / 60) + 'm ago'
  if (seconds < 86400)                     return Math.floor(seconds / 3600) + 'h ago'
  if (seconds < 86400 * 30)               return Math.floor(seconds / 86400) + 'd ago'
  if (seconds < 86400 * 365)              return Math.floor(seconds / (86400 * 30)) + 'mo ago'
  return Math.floor(seconds / (86400 * 365)) + 'y ago'
}


const showModal = ref(false)
const submitting = ref(false)

const emptyForm = () => ({
  email: '',
  name: '',
  business_name: '',
  business_address: '',
  business_phone_number: '',
  password: '',
  password_confirmation: '',
})

const form = ref(emptyForm())

function closeModal() {
  showModal.value = false
  form.value = emptyForm()
}

function loginAs(userId) {
  router.post('/admin/impersonation', { user_id: userId })
}

function submitForm() {
  submitting.value = true
  router.post('/admin/users', { user: form.value }, {
    onFinish: () => { submitting.value = false },
    onSuccess: () => { closeModal() },
  })
}
</script>
